import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

export type ConfirmStatusDialogProps = {
  open: boolean;
  currentValue: boolean;
  customerName?: string;
  itemDescription?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const LABEL_SX = {
  fontSize: 10.5,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.7px',
  color: 'text.disabled',
};

export const ConfirmStatusDialog = ({
  open,
  currentValue,
  customerName,
  itemDescription,
  onConfirm,
  onCancel,
}: ConfirmStatusDialogProps) => {
  const targetState = currentValue ? 'Incomplete' : 'Complete';
  const targetStateLower = targetState.toLowerCase();
  const hasContext = Boolean(customerName || itemDescription);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 460,
          width: '100%',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, fontSize: 17, fontWeight: 700 }}>
        Mark as {targetState}?
      </DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        {hasContext && (
          <Box
            sx={{
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              px: 1.75,
              py: 1.25,
              mb: 1.75,
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 2,
              rowGap: 0.75,
              alignItems: 'baseline',
            }}
          >
            {customerName && (
              <>
                <Typography component="span" sx={LABEL_SX}>
                  Customer
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, fontSize: 14, color: 'text.primary' }}
                >
                  {customerName}
                </Typography>
              </>
            )}
            {itemDescription && (
              <>
                <Typography component="span" sx={LABEL_SX}>
                  Item
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontWeight: 500, fontSize: 14, color: 'text.primary' }}
                >
                  {itemDescription}
                </Typography>
              </>
            )}
          </Box>
        )}
        <DialogContentText sx={{ color: 'text.secondary' }}>
          Are you sure you want to mark this item as {targetStateLower}? You can
          revert this later by clicking the status pill again.
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button onClick={onCancel} variant="text" sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" autoFocus>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
